/**
 * Decision gate contract — a neutral, producer-agnostic surface for
 * human-in-the-loop decision points ("gates").
 *
 * A "decision gate" is any point where a workflow pauses and waits for a
 * person to make a choice (approve / reject / pick an option) before it
 * continues. The flow engine's approval node is the first producer behind
 * this contract, but the contract deliberately knows nothing about flows so
 * that delivery/answer surfaces (the inbox, Slack, email, …) can list and
 * resolve gates without depending on `plugin-flows`.
 *
 * The same gate is resolvable from any surface: whichever surface answers
 * first wins, and an `onResolved` event lets the other surfaces reflect the
 * outcome (e.g. a Slack message disables its buttons once the gate is
 * answered in the inbox).
 *
 * @module @reflex/plugin-api/services/decision-gate
 */
import type { ActorId } from '@reflex/shared';
import type { OwnerScopeCaller } from '../owner-scope.js';

/** WS `flowEventType` broadcast when a decision gate opens or resolves. */
export const DECISION_GATE_WS_EVENT = 'decision_gate:updated';

/** Gate `kind` used by the flow engine's approval nodes. */
export const FLOW_APPROVAL_GATE_KIND = 'flow_approval';

/** An approval instance; legacy IDs may address only the first iteration. */
export function flowApprovalGateId(runId: string, nodeId: string, iteration: number): string {
  // Preserve the identity of already-posted first-iteration messages. A
  // legacy id always means iteration 1, never whichever iteration is open.
  if (iteration === 1 && !runId.includes(':'))
    return `${FLOW_APPROVAL_GATE_KIND}:${runId}:${nodeId}`;
  return `flow_approval_v2:${encodeURIComponent(runId)}:${encodeURIComponent(nodeId)}:${iteration}`;
}

export function parseFlowApprovalGateId(
  gateId: string,
): { runId: string; nodeId: string; iteration?: number } | null {
  if (typeof gateId !== 'string') return null;
  if (gateId.startsWith('flow_approval_v2:')) {
    const parts = gateId.slice('flow_approval_v2:'.length).split(':');
    if (parts.length !== 3 || !/^[1-9]\d*$/.test(parts[2]!)) return null;
    try {
      const runId = decodeURIComponent(parts[0]!);
      const nodeId = decodeURIComponent(parts[1]!);
      const iteration = Number(parts[2]);
      return runId && nodeId && Number.isSafeInteger(iteration)
        ? { runId, nodeId, iteration }
        : null;
    } catch {
      return null;
    }
  }
  const prefix = `${FLOW_APPROVAL_GATE_KIND}:`;
  if (!gateId.startsWith(prefix)) return null;
  const rest = gateId.slice(prefix.length);
  const sep = rest.indexOf(':');
  if (sep <= 0 || sep >= rest.length - 1) return null;
  return { runId: rest.slice(0, sep), nodeId: rest.slice(sep + 1) };
}

/** Payload carried by a DECISION_GATE_WS_EVENT broadcast. */
export interface DecisionGateUpdatedPayload {
  organizationId: string;
}

/** A single selectable decision on a multi-way gate. */
export interface DecisionGateOption {
  /** Routing value returned when this option is chosen. */
  value: string;
  /** Human-readable button label. */
  label: string;
}

/**
 * Who a gate is routed to and gated on. Mirrors the flow approval
 * `reportTo` model so producers can map their own assignee shape onto a
 * neutral one. An empty `assignees` array means "anyone may answer".
 */
export type DecisionGateAssignee =
  | { kind: 'user'; userId: string }
  | { kind: 'team'; teamId: string }
  | { kind: 'organization' };

/**
 * A pending decision gate as seen by a delivery/answer surface.
 *
 * `context` is an opaque, producer-defined bag of reviewer context (e.g. the
 * flow node's prompt context, completed steps, trigger data) that surfaces
 * may render but should not depend on structurally.
 */
export interface DecisionGate {
  /**
   * Stable, globally-unique gate id of the form `<kind>:<producerRef>` — the
   * `kind` prefix lets the registry route a decision back to the producer
   * that opened it (e.g. `flow_approval_v2:<runId>:<nodeId>:<iteration>`).
   */
  id: string;
  /** Producer-defined kind, e.g. `flow_approval`. */
  kind: string;
  /** Organization that owns the gate. */
  organizationId: string;
  /** Short title for the gate (e.g. the node name). */
  title: string;
  /** Instruction/prompt text shown to the reviewer. */
  prompt: string;
  /**
   * Available decisions. Empty means a binary approve/reject gate (surfaces
   * should render Approve/Reject and submit `approved`/`rejected`).
   */
  options: DecisionGateOption[];
  /** Whether the surface should offer a free-text feedback field. */
  allowFeedback: boolean;
  /** Effective targets the gate is routed to / gated on. */
  assignees: DecisionGateAssignee[];
  /** Opaque reviewer context for rich rendering. */
  context?: Record<string, unknown> | null;
  /** Epoch millis when the gate opened. */
  createdAt: number;
}

/**
 * Identity of the actor answering a gate — the host owner fence itself.
 * The alias exists for the contract's vocabulary; there is no separate
 * gate-caller shape, so producers reuse their assignee authz on it
 * directly and surfaces hand over what the authz builders return.
 */
export type DecisionGateCaller = OwnerScopeCaller;

/** The answer submitted for a gate. */
export interface DecisionGateResolution {
  /** The chosen option value, or `approved` / `rejected` for binary gates. */
  decision: string;
  /** Optional free-text feedback. */
  feedback?: string;
  /** Where the answer originated, e.g. `inbox` or `slack`. */
  via: string;
  /**
   * Principal who answered — a member or service account. Stamped
   * server-side from the authenticated caller (submitted values are
   * ignored); optional for historical resolutions or other producers. New flow approvals require a principal.
   */
  decidedBy?: ActorId;
}

/** Unsubscribe handle returned by the registry's event subscriptions. */
export type DecisionGateUnsubscribe = () => void;

/**
 * Thrown by `submitDecision` when the gate has already been resolved (e.g. a
 * race between Slack and the inbox). Carries `statusCode` 409 so HTTP routes
 * can surface it directly.
 */
export class DecisionGateAlreadyResolvedError extends Error {
  readonly statusCode = 409;
  constructor(gateId: string) {
    super(`Decision gate "${gateId}" has already been resolved`);
    this.name = 'DecisionGateAlreadyResolvedError';
  }
}

/**
 * Neutral registry that decouples gate producers (flows, …) from gate
 * surfaces (inbox, Slack, …). Implemented by `plugin-flows` and exposed on
 * `ctx.services.decisionGates`; consumed by any plugin via this contract.
 */
export interface DecisionGateRegistryContract {
  /**
   * List the gates currently pending for an org that the caller is allowed
   * to answer (assignee-scoped). Producers may fail closed (403) when the
   * caller is absent.
   */
  listPending(organizationId: string, caller?: DecisionGateCaller): Promise<DecisionGate[]>;

  /**
   * Resolve a gate. Idempotent: a second answer for an already-resolved gate
   * throws {@link DecisionGateAlreadyResolvedError}. Authorization is enforced
   * by the producer against the gate's assignees; producers may fail closed
   * (403) when the caller is absent.
   */
  submitDecision(
    gateId: string,
    resolution: DecisionGateResolution,
    organizationId: string,
    caller?: DecisionGateCaller,
  ): Promise<void>;

  /** Subscribe to gate-opened events. Returns an unsubscribe handle. */
  onOpened(handler: (gate: DecisionGate) => void): DecisionGateUnsubscribe;

  /** Subscribe to gate-resolved events. Returns an unsubscribe handle. */
  onResolved(
    handler: (gate: DecisionGate, resolution: DecisionGateResolution) => void,
  ): DecisionGateUnsubscribe;
}
